import { contentDb, type PageDetailRow, isDetailFreshForCurrentBuild } from '@/db/content';
import { getExpectedCacheGeneration, shouldSendHttpCacheValidators } from '@/db/contentCache';
import {
  extractDetailSliceFromConsolidated,
  findConsolidatedRowByPageId,
  maxDetailDateForRequest,
  mergeApiBundleIntoRow,
} from './consolidatedIdb';

// Use relative path so dev/preview proxy and production same-origin handle /api
const API_BASE = '';
export const PAGES_URL = `${API_BASE}/api/v2/pages/`;
const PAGE_PREVIEW_URL = `${API_BASE}/api/v2/page_preview/`;

/** In dev, wait to simulate network latency. */
export function devFetchDelay(delay: number = 500): Promise<void> {
  if (import.meta.env.DEV) {
    return new Promise((resolve) => setTimeout(resolve, delay));
  }
  return Promise.resolve();
}

function detailDateFromPayload(raw: Record<string, unknown>): string | null {
  const meta = raw.meta as { last_published_at?: string | null } | undefined;
  return meta?.last_published_at ?? null;
}

/**
 * Fetch one page detail from the API, optionally with If-Modified-Since from cached slice.
 * Persists one consolidated IndexedDB row per translation_key. Returns the slice for `pageId`.
 */
export async function fetchPageDetailFromApi(
  pageId: number,
  cached?: PageDetailRow
): Promise<PageDetailRow | null> {
  const existingRow = await findConsolidatedRowByPageId(pageId);
  const headers: HeadersInit = {};
  if (shouldSendHttpCacheValidators()) {
    const ims =
      cached?.detailDate && isDetailFreshForCurrentBuild(cached)
        ? cached.detailDate
        : maxDetailDateForRequest(existingRow) ?? undefined;
    if (ims) {
      headers['If-Modified-Since'] = new Date(ims).toUTCString();
    }
  }
  try {
    const response = await fetch(`${PAGES_URL}${pageId}/`, {
      credentials: 'same-origin',
      cache: 'no-store',
      headers,
    });
    if (response.status === 304) {
      if (cached) return cached;
      if (existingRow) {
        return extractDetailSliceFromConsolidated(existingRow, pageId);
      }
      return null;
    }
    const raw = response.ok ? ((await response.json()) as Record<string, unknown>) : null;
    if (!raw) {
      if (existingRow) {
        try {
          await contentDb.page.delete(existingRow.translation_key);
        } catch {
          // ignore
        }
      }
      return null;
    }

    const gen = getExpectedCacheGeneration();

    if ('locales' in raw && raw.locales && typeof raw.locales === 'object') {
      const merged = mergeApiBundleIntoRow(existingRow, raw);
      const stamped = { ...merged, detailClientGeneration: gen };
      try {
        await contentDb.page.put(stamped);
      } catch {
        // Ignore cache write failures
      }
      return extractDetailSliceFromConsolidated(stamped, pageId) ?? null;
    }

    return {
      ...raw,
      id: pageId,
      title: typeof raw.title === 'string' ? raw.title : '',
      meta: (raw.meta ?? {}) as import('@/db/content').PageItem['meta'],
      detailDate: detailDateFromPayload(raw),
      detailClientGeneration: gen,
    } as PageDetailRow;
  } catch {
    return cached ?? null;
  }
}

/**
 * Fetch draft page for headless preview. Does not cache in IndexedDB.
 * Returns page JSON (single-locale shape) or null on error.
 */
export async function fetchPagePreview(
  contentType: string,
  token: string
): Promise<PageDetailRow | null> {
  const params = new URLSearchParams({ content_type: contentType, token });
  try {
    const response = await fetch(`${PAGE_PREVIEW_URL}?${params}`, {
      credentials: 'same-origin',
      cache: 'no-store',
    });
    if (!response.ok) return null;
    const data = (await response.json()) as Record<string, unknown>;
    const meta = data.meta as { last_published_at?: string | null } | undefined;
    return {
      ...data,
      id: (data.id as number) ?? 0,
      detailDate: meta?.last_published_at ?? null,
    } as PageDetailRow;
  } catch {
    return null;
  }
}
